import {
	ChannelAnalogInputUseMode,
	ChannelAnalogLevel,
	ChannelGroup,
	ChannelLoudspeaker,
	ChannelOutputPath,
	ChannelPcPeripheral,
	DanteAudioEncryptionStatus,
	DeviceMicState,
	InputSource,
	MissingFeatureType,
	NumberRange,
	DeviceStatus,
	SpatialSeparation
} from '@shure/shared/angular/data-access/system-api/models';

export interface DspDeviceChannelDspFeatures {
	acousticEchoCancellation?: { enabled: boolean } | null;
	automaticGainControl?: { enabled: boolean } | null;
	compressor?: { enabled: boolean } | null;
	delay?: { enabled: boolean } | null;
	equalizer?: { enabled: boolean; isPeq: boolean; gainStepConstraint: number | undefined } | null;
	limiter?: { enabled: boolean } | null;
	noiseReduction?: { enabled: boolean } | null;
	denoiser?: { enabled: boolean } | null;
	deverb?: { enabled: boolean } | null;
}

export type DspDeviceChannelDspKind = keyof DspDeviceChannelDspFeatures;

export interface DspDeviceChannelConstraints {
	gain: NumberRange;
	analogLevel?: ChannelAnalogLevel[];
	meter: NumberRange;
	inputSource: {
		options: InputSource[];
	};
	outputPath: {
		options: ChannelOutputPath[];
	};
	passiveSpeakers: {
		options: ChannelLoudspeaker[];
	};
}

export interface RfChannelFeatures {
	device: {
		id: string;
		status: DeviceMicState;
		deviceState: DeviceStatus;
	};
}

export interface DspDeviceChannelFeatures {
	name: string;
	gain?: number;
	muted: boolean;
	postGateMute?: {
		muted: boolean;
	} & MissingFeatureType;
	danteEncryptionStatus: DanteAudioEncryptionStatus;
	analogLevel?: ChannelAnalogLevel;
	autoMute?: boolean;
	pcPeripheral?: {
		options: ChannelPcPeripheral[];
		selected?: ChannelPcPeripheral;
	};
	hasStereoMeter: boolean;
	inputSource?: {
		source: InputSource;
	};
	outputPath?: {
		path: ChannelOutputPath;
	};
	passiveSpeakers?: {
		type: ChannelLoudspeaker;
	};
	channelConfiguration?: {
		enabled: boolean;
	} & MissingFeatureType;
	rfChannel?: {
		id: string;
		features: RfChannelFeatures;
	} & MissingFeatureType;
	analogInputUse?: {
		mode: ChannelAnalogInputUseMode;
	} & MissingFeatureType;
	channelBonding?: {
		spatialSeparation: SpatialSeparation;
		bondedChannelId?: string;
	} & MissingFeatureType;
}

export interface DspDeviceChannel {
	id: string;
	group: ChannelGroup;
	disabled?: boolean;
	constraints: DspDeviceChannelConstraints;
	features: DspDeviceChannelFeatures & { dsp: DspDeviceChannelDspFeatures };
}

export interface DspDeviceChannelMetering {
	leftOrMonoValue: number;
	leftOrMonoPeak: number;
	rightValue?: number;
	rightPeak?: number;
}

/**
 * Type of channel
 *
 * Note:
 * Used for simplifying styling via. tokens from `schematics.tokens.scss`.
 * **Do not change without verifying theming still applies**
 */
export enum DspChannelType {
	Input = 'input-channel',
	Output = 'output-channel',
	Direct = 'direct-channel'
}
